"use client";
import React, { useContext, useEffect } from "react";
import styled from "styled-components";
import { device, useBreakpoints } from "../../lib/breakpoints";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { trackEvent } from "../../helpers/mixpanel";
import { AuthContext } from "../../context/AuthProvider";
import { parseHeroAddressParam } from "../../lib/heroAddressParam";
import { HOME_SAVINGS_TIER_ROWS } from "../../lib/homeSavingsShowcaseData";
import HomeHeroChatDemo from "../../components/home/HomeHeroChatDemo";
import ProtocolSequenceSection from "../../components/home/ProtocolSequenceSection";
import { CheckOutlined } from "@ant-design/icons";

// ─── Design tokens ────────────────────────────────────────────────────────────
const IND_ORANGE = "#FF5722";
const SLATE_800 = "#1e293b";
const SLATE_700 = "#334155";
const SLATE_600 = "#475569";
const SLATE_500 = "#64748b";
const SLATE_300 = "#cbd5e1";
const WHITE = "#ffffff";
const BLACK = "#000000";

import { HOME_HERO_TAGLINE_LINES, HOME_HERO_DESCRIPTION } from "../../helpers/constants";

const HomeMain = ({ openIntake = () => {} }) => {
  const { isMobile } = useBreakpoints();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const addressFromQuery = parseHeroAddressParam(location.search);
  const heroPropertyLabel = addressFromQuery ?? "your portfolio";
  useEffect(() => {
    if (location.state?.openFreeIntake) {
      openIntake({ plan: "retail" });
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [
    location.state,
    location.pathname,
    location.search,
    navigate,
    openIntake,
  ]);

  const handleHeroPrimaryCta = () => {
    if (user) {
      trackEvent("Hero Chat With Clumse Click", {
        section: "hero",
        device_type: isMobile ? "mobile" : "desktop",
      });
      navigate("/chat");
      return;
    }
    trackEvent("Hero Initiate Dispatch Click", {
      section: "hero",
      plan: "retail",
      device_type: isMobile ? "mobile" : "desktop",
      cta: "initiate_dispatch_25",
    });
    openIntake({ plan: "retail" });
  };

  return (
    <Container data-home-main>
      <DarkFieldStack>
        <DashboardViewport>
          {/* ── Hero Band ──────────────────────────────────────────────────── */}
          <HeroBand>
            <MainShell as="section" aria-label="How Clumse works">
              <HeroGrid>
                <HeroLeft>
                  <HeroCopy className="hero-content surface-dark-copy">
                    <HeroCopyPanel>
                      <HeroControlBlock>
                        <Title id="main-heading" className="hero-title">
                          {HOME_HERO_TAGLINE_LINES.map((line) => (
                            <TitleLine key={line}>{line}</TitleLine>
                          ))}
                        </Title>

                        <HeroBody>{HOME_HERO_DESCRIPTION}</HeroBody>

                        {addressFromQuery ? (
                          <HeroPersonalization as="p">
                            Preview for {heroPropertyLabel}
                          </HeroPersonalization>
                        ) : null}

                        <HeroTrustStrip aria-label="What you get">
                          <HeroTrustItem>
                            <CheckOutlined aria-hidden />
                            Free diagnosis & repair estimates
                          </HeroTrustItem>
                          <HeroTrustItem>
                            <CheckOutlined aria-hidden />
                            Three vetted bids per search
                          </HeroTrustItem>
                          <HeroTrustItem>
                            <CheckOutlined aria-hidden />
                            Benchmarked labor & parts
                          </HeroTrustItem>
                        </HeroTrustStrip>

                        <HeroCtaCluster>
                          <HeroTagline>
                            No payment required to start.
                          </HeroTagline>

                          {/* ── Primary CTA: fixed rhythm below fine print ───── */}
                          <HeroActionStack>
                            <HeroActionUpper>
                              {!user ? (
                                <HeroIntakeStretch>
                                  <PrimaryButton
                                    type="button"
                                    onClick={handleHeroPrimaryCta}
                                    aria-label="Get started with free diagnosis"
                                  >
                                    GET STARTED
                                  </PrimaryButton>
                                </HeroIntakeStretch>
                              ) : (
                                <HeroIntakeStretch>
                                  <PrimaryButton
                                    type="button"
                                    onClick={handleHeroPrimaryCta}
                                    aria-label="Chat with Clumse AI"
                                  >
                                    Chat with Clumse AI
                                  </PrimaryButton>
                                </HeroIntakeStretch>
                              )}
                            </HeroActionUpper>
                          </HeroActionStack>
                        </HeroCtaCluster>
                      </HeroControlBlock>
                    </HeroCopyPanel>
                  </HeroCopy>
                </HeroLeft>

                <HeroRight>
                  <HeroDeskStack
                    className="hero-widget-wrapper"
                    data-testid="home-hero-chat-demo"
                  >
                    <HomeHeroChatDemo addressPreview={addressFromQuery} />
                  </HeroDeskStack>
                </HeroRight>
              </HeroGrid>
            </MainShell>
          </HeroBand>

          <IndustrialFooterTelemetry>
            <IndustrialFooterInner>
              <IndustrialFooterLeft>
                National platform · Local benchmarks
              </IndustrialFooterLeft>
              <IndustrialFooterStar aria-hidden="true" />
            </IndustrialFooterInner>
          </IndustrialFooterTelemetry>
        </DashboardViewport>

        <HeroFloorDivider aria-hidden="true" />

        <ProtocolSequenceSection id="the-protocol" />
      </DarkFieldStack>

      {/* ── Savings Demonstration Section ──────────────────────────────── */}
      <SavingsShowcaseSection aria-label="Pricing examples: competitive bid ranges">
        <MainShell>
          <SavingsContent>
            <SavingsHeader>
              <SavingsTitle>SAVINGS BY WORK TYPE</SavingsTitle>
              <SavingsSubtitle>
                <SavingsSubtitleLine>
                  Each card shows a typical job type, a high retail benchmark
                  vs. a tighter bid,
                </SavingsSubtitleLine>
                <SavingsSubtitleLine>
                  and what you could keep in your pocket (flat $49 search fee).
                </SavingsSubtitleLine>
              </SavingsSubtitle>
            </SavingsHeader>

            <SavingsExamples>
              {HOME_SAVINGS_TIER_ROWS.map((row) => {
                const savings = Math.max(0, row.marketBid - row.targetBid);
                return (
                  <SavingsExample key={row.id}>
                    <LedgerEndCap $variant="search" aria-hidden>
                      <LedgerMetricAmount $tone="orange">
                        <span className="symbol">$</span>
                        {row.searchFee}
                      </LedgerMetricAmount>
                      <LedgerMetricLabel $tone="orange">
                        Search
                      </LedgerMetricLabel>
                    </LedgerEndCap>

                    <LedgerJobInfo>
                      <TierLabel>{row.tierLabel}</TierLabel>
                      <JobTitle>{row.exampleTitle}</JobTitle>
                      <JobDesc>{row.exampleDesc}</JobDesc>
                      <WorkTypesLine>{row.workTypesLine}</WorkTypesLine>
                    </LedgerJobInfo>

                    <LedgerPricing>
                      <PriceColumn $variant="retail">
                        <PriceLabel $isRetail>High Market Bid</PriceLabel>
                        <PriceAmount $isExpensive>
                          <span className="symbol">$</span>
                          {row.marketBid.toLocaleString()}
                          <span className="symbol suffix">+</span>
                        </PriceAmount>
                      </PriceColumn>

                      <VsIndicatorLedger>vs</VsIndicatorLedger>

                      <PriceColumn $variant="clumse">
                        <PriceLabel>Secured Target Bid</PriceLabel>
                        <PriceAmount $isReasonable>
                          <span className="symbol">~</span>
                          <span className="symbol">$</span>
                          {row.targetBid.toLocaleString()}
                        </PriceAmount>
                      </PriceColumn>
                    </LedgerPricing>

                    <LedgerEndCap $variant="saved">
                      <LedgerMetricAmount $tone="green">
                        <span className="symbol">$</span>
                        {savings.toLocaleString()}
                        <span className="symbol suffix">+</span>
                      </LedgerMetricAmount>
                      <LedgerMetricLabel $tone="green">Saved</LedgerMetricLabel>
                    </LedgerEndCap>
                  </SavingsExample>
                );
              })}
            </SavingsExamples>

            <SavingsFooter>
              <SavingsCallout>
                <CalloutText>
                  <CalloutTitle>
                    The difference? Vetted pros and sharper bids.
                  </CalloutTitle>
                  <CalloutDesc>
                    We streamline the bidding process by delivering
                    crystal-clear, ready-to-bid project data to top local pros.
                    You get quality work at a fair price for your market.
                  </CalloutDesc>
                </CalloutText>
              </SavingsCallout>

              <SafetyDisclaimer>
                Illustrative examples only. Real costs and savings depend on
                scope, urgency, location, and availability.
              </SafetyDisclaimer>
            </SavingsFooter>
          </SavingsContent>
        </MainShell>
      </SavingsShowcaseSection>
    </Container>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
  padding: 0;
  /* Top spacing vs fixed nav; keep tight under industrial nav */
  margin-top: clamp(0.25rem, 1vw, 0.75rem);
  margin-bottom: 0;
  background: ${BLACK};
  /* clip avoids horizontal bleed without establishing a scroll box that breaks sticky nav */
  overflow-x: clip;
  width: 100%;
  max-width: 100vw;
`;

/** Shared horizontal rail: max-w-screen-2xl (1536px) + px-16 gutters. */
const MainShell = styled.div`
  position: relative;
  width: 100%;
  max-width: 1536px;
  margin: 0 auto;
  padding-left: clamp(1rem, 4vw, 4rem);
  padding-right: clamp(1rem, 4vw, 4rem);
  box-sizing: border-box;
`;

/** Solid black field behind hero + protocol + pricing (no decorative grid on home). */
const DarkFieldStack = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: ${BLACK};
`;

const DashboardViewport = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: ${BLACK};
  box-sizing: border-box;
  width: 100%;
`;

/** Tight vertical stack: headline → body → intake → protocols CTA */
const HeroControlBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: inherit;
  width: 100%;
  gap: 0.35rem;
  margin-top: 0.4rem;

  @media (min-width: 1024px) {
    gap: clamp(0.55rem, 1vw, 0.95rem);
    margin-top: 0;
    max-width: 100%;
  }

  @media ${device.mobile} {
    gap: 0.45rem;
    margin-top: 0.2rem;
  }
`;

/** Copy stack: no panel chrome; headline and trust list sit on the grid */
const HeroCopyPanel = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;

  @media (min-width: 1024px) {
    max-width: min(38rem, 100%);
  }
`;

const HeroTrustStrip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.45rem;
  margin-top: 0.15rem;
  width: 100%;
  max-width: 36rem;

  @media ${device.mobile} {
    align-items: center;
  }

  @media (min-width: 1024px) {
    margin-top: 0.1rem;
    max-width: min(38rem, 100%);
  }
`;

const HeroTrustItem = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: 0.45rem;
  font-family: var(--font-sans, "Inter", system-ui, sans-serif);
  font-size: clamp(0.8rem, 1.05vw, 0.9rem);
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0.01em;
  color: rgba(226, 232, 240, 0.95);
  text-align: left;

  .anticon {
    flex-shrink: 0;
    margin-top: 0.15em;
    font-size: 0.78rem;
    color: #4ade80;
  }

  @media ${device.mobile} {
    font-size: 0.82rem;
  }
`;

const IndustrialFooterTelemetry = styled.footer`
  flex-shrink: 0;
  width: 100%;
  border-top: 1px solid ${SLATE_800};
  background: ${BLACK};
  box-sizing: border-box;
`;

const IndustrialFooterInner = styled.div`
  max-width: 1536px;
  margin: 0 auto;
  padding: 0.85rem clamp(1rem, 4vw, 4rem) 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  box-sizing: border-box;
`;

const IndustrialFooterLeft = styled.span`
  font-family: var(--font-sans, "Inter", system-ui, sans-serif);
  font-size: clamp(0.75rem, 1.2vw, 0.85rem);
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(248, 250, 252, 0.6);
  line-height: 1.4;
`;

const IndustrialFooterStar = styled.span`
  display: block;
  width: 13px;
  height: 13px;
  flex-shrink: 0;
  background: #00c853;
  clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
  box-shadow: 0 0 16px rgba(0, 200, 83, 0.9);
`;

const HeroEyebrow = styled.p`
  margin: 0;
  max-width: 42rem;
  width: 100%;
  font-family: var(--font-sans, "Inter", system-ui, sans-serif);
  font-size: clamp(0.8rem, 1.2vw, 0.9rem);
  font-weight: 800;
  letter-spacing: 0.14em;
  line-height: 1.25;
  text-transform: uppercase;
  color: ${IND_ORANGE};
  text-align: inherit;
`;

const HeroBody = styled.p`
  margin: 0;
  max-width: min(40rem, 100%);
  width: 100%;
  font-family: var(--font-sans, "Inter", system-ui, sans-serif);
  font-size: clamp(0.95rem, 1.35vw, 1.12rem);
  font-weight: 500;
  line-height: 1.65;
  letter-spacing: 0.01em;
  text-transform: none;
  color: rgba(255, 255, 255, 0.94);
  text-align: inherit;

  @media (min-width: 1024px) {
    max-width: 100%;
  }
`;

/** Fine print + CTA: explicit 1.5rem rhythm between tagline and primary button. */
const HeroCtaCluster = styled.div`
  display: flex;
  flex-direction: column;
  align-items: inherit;
  width: 100%;
  max-width: 36rem;
  gap: 1.5rem;
  margin-top: 0.35rem;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 1024px) {
    margin-left: 0;
    margin-right: 0;
    margin-top: 0.5rem;
    max-width: 100%;
  }
`;

const HeroTagline = styled.p`
  margin: 0;
  max-width: 36rem;
  width: 100%;
  font-family: var(--font-sans, "Inter", system-ui, sans-serif);
  font-size: clamp(0.78rem, 1.05vw, 0.875rem);
  font-weight: 500;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: 0.02em;
  color: #94a3b8;
  text-align: inherit;

  @media (min-width: 1024px) {
    max-width: 100%;
  }
`;

const HeroFloorDivider = styled.div`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  height: 1px;
  flex-shrink: 0;
  background: ${SLATE_800};
  box-shadow: none;
`;

const HeroBand = styled.div`
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  z-index: 1;
  border-bottom: none;
  /* MainLayout already offsets for the fixed nav; keep a modest rhythm only. */
  padding-top: clamp(0.35rem, 1vw, 0.75rem);
  /* Breathing room before telemetry strip / divider */
  padding-bottom: clamp(3.5rem, 9vw, 6.5rem);
  background-color: ${BLACK};
  background-image: linear-gradient(
      to right,
      rgba(15, 23, 42, 0.5) 1px,
      transparent 1px
    ),
    linear-gradient(to bottom, rgba(15, 23, 42, 0.5) 1px, transparent 1px);
  background-size: 28px 28px;
  background-position: center top;

  @media (min-width: 1024px) {
    padding-top: clamp(0.5rem, 1.25vw, 1rem);
  }

  @media ${device.mobile} {
    padding-top: clamp(0.5rem, 3vw, 1rem);
    padding-bottom: clamp(3rem, 10vw, 5rem);
  }
`;

const HeroGrid = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2rem;
  width: 100%;
  padding-top: 0.5rem;
  padding-bottom: clamp(0.5rem, 1.5vw, 1rem);

  @media (min-width: 1024px) {
    display: grid;
    /* ~55 / 45: slightly more width to the execution stack than the old 60/40 */
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    /* Shared row height; left and right columns each vertically center their content. */
    align-items: stretch;
    justify-content: stretch;
    column-gap: clamp(1.75rem, 4vw, 3.25rem);
    row-gap: 0;
    padding-top: 0;
  }
`;

const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.25rem 0 0;

  @media (min-width: 1024px) {
    width: 100%;
    max-width: none;
    display: flex;
    flex-direction: column;
    align-self: stretch;
    min-height: 0;
    padding: 0;
  }
`;

const HeroRight = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  align-items: stretch;
  justify-content: flex-start;
  padding-bottom: clamp(1rem, 3vw, 1.5rem);

  @media (min-width: 1024px) {
    width: 100%;
    max-width: min(36rem, 100%);
    min-height: 0;
    align-items: stretch;
    justify-self: start;
    /* Match left column: center the execution stack in the shared row height */
    justify-content: center;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const HeroCopy = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  text-align: center;

  @media (min-width: 1024px) {
    align-items: flex-start;
    text-align: left;
    flex: 1 1 auto;
    min-height: 0;
    /* Row height follows the right column; center copy block in that band */
    justify-content: center;
  }
`;

const HeroIntakeStretch = styled.div`
  width: 100%;
  max-width: 100%;
  align-self: stretch;
`;

/** Intake / system-ready block (fixed height; does not absorb vertical slack). */
const HeroActionUpper = styled.div`
  flex-shrink: 0;
  width: 100%;
`;

const Title = styled.h1`
  /* Inter extrabold (800); TitleLine uses inherit so globals.css span rule does not shrink lines */
  font-size: clamp(2.5rem, 2.5vw + 1.35rem, 3.75rem);
  font-weight: 800;
  line-height: 1.12;
  margin: 0;
  letter-spacing: -0.02em;
  color: ${WHITE};
  font-family: var(--font-sans, "Inter", system-ui, sans-serif);
  text-transform: uppercase;
  text-align: inherit;
  max-width: 100%;
  overflow-wrap: normal;
  word-break: normal;
  -webkit-font-smoothing: antialiased;

  /* Two-column hero: line 1 + “A HOME REPAIR AGAIN” on line 2; fluid size for nowrap */
  @media (min-width: 1024px) {
    font-size: clamp(1.75rem, 2.45vw + 0.62rem, 3.45rem);
  }

  @media ${device.mobile} {
    font-size: clamp(1.65rem, 5.5vw + 0.55rem, 2.45rem);
    line-height: 1.18;
    letter-spacing: -0.02em;
    max-width: 100%;
  }
`;

const TitleLine = styled.span`
  display: block;
  /* Critical: globals.css span 1.125rem would override h1 sizing on these lines */
  font-size: inherit;
  line-height: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  font-family: inherit;
  text-transform: inherit;
  color: inherit;

  &:not(:last-child) {
    margin-bottom: 0.1em;
  }

  /* Desktop: fixed two-line lockup */
  @media (min-width: 1024px) {
    white-space: nowrap;
  }

  /* Mobile: one block; wrap only at word boundaries, no forced mid-phrase splits */
  @media ${device.mobile} {
    max-width: 100%;
    hyphens: manual;
  }
`;

const HeroDiagnosticLine = styled.div`
  font-family: var(--font-sans, "Inter", system-ui, sans-serif);
  font-size: clamp(0.75rem, 1.2vw, 0.85rem);
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(248, 250, 252, 0.55);
  text-align: inherit;
`;

const HeroPersonalization = styled.p`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${IND_ORANGE};
  font-family: var(--font-sans, "Inter", system-ui, sans-serif);
  text-align: inherit;
  max-width: 580px;
  width: 100%;
  line-height: 1.4;

  @media ${device.mobile} {
    font-size: 0.88rem;
  }
`;

const HeroActionStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0;
  width: 100%;
  max-width: 100%;

  @media (min-width: 1024px) {
    align-items: stretch;
    flex: 0 0 auto;
    min-height: 0;
    gap: 0.35rem;
  }

  @media ${device.mobile} {
    gap: 0.4rem;
  }
`;

const PrimaryButton = styled.button`
  background: ${IND_ORANGE};
  color: ${WHITE};
  border: 2px solid #ffffff;
  padding: 0.9rem 1.35rem;
  border-radius: 0;
  font-size: clamp(0.9rem, 2.2vw, 1.05rem);
  font-weight: 600;
  cursor: pointer;
  transition: transform 100ms steps(2, end), background 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: min(100%, 265px);
  width: auto;
  min-height: 50px;
  box-shadow: none;
  font-family: var(--font-sans, "Inter", system-ui, sans-serif);
  letter-spacing: 0.01em;
  text-transform: none;

  &:hover {
    transform: none;
    background: #f44e1b;
  }
  &:active {
    transform: scale(0.99);
    background: #e84615;
  }

  @media ${device.mobile} {
    min-width: 0;
    width: 100%;
    font-size: 0.9rem;
    padding: 0.85rem 1rem;
  }
`;

const HeroCtaStack = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  text-align: center;
  line-height: 1.2;
`;

const CtaSubline = styled.span`
  font-size: 0.78em;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: rgba(255, 255, 255, 0.9);
  max-width: 20rem;
`;

const HeroDeskStack = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: transparent;
  overflow: hidden;

  @media (min-width: 1024px) {
    flex: 0 1 auto;
    width: 100%;
    max-width: 100%;
    min-height: 0;
    align-self: stretch;
    margin-top: 0;
  }

  @media ${device.mobile} {
    margin: 0 auto;
  }
`;

// ─── Savings Showcase Section ────────────────────────────────────────────────

const SavingsShowcaseSection = styled.section`
  position: relative;
  width: 100%;
  background: linear-gradient(135deg, #ff5722 0%, #f44336 100%);
  padding: 4rem 0 5rem;
  margin: 0;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 30%,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 70%,
        rgba(255, 255, 255, 0.08) 0%,
        transparent 50%
      );
    pointer-events: none;
  }
`;

const SavingsContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SavingsHeader = styled.div`
  text-align: center;
  max-width: 600px;

  @media (min-width: 769px) {
    max-width: 58rem;
  }
`;

const SavingsTitle = styled.h2`
  font-size: clamp(2.2rem, 4vw, 3.2rem);
  font-weight: 900;
  color: ${WHITE};
  margin: 0 0 1rem;
  letter-spacing: -0.02em;
  line-height: 1.1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const SavingsSubtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  line-height: 1.5;
  font-weight: 400;

  @media (min-width: 769px) {
    font-size: 1.2rem;
  }
`;

const SavingsSubtitleLine = styled.span`
  display: inline;

  @media (min-width: 1024px) {
    display: block;
  }
`;

const SavingsExamples = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 1200px;
`;

const SavingsExample = styled.div`
  background: rgba(255, 255, 255, 0.96);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
  }

  /* Mobile: centered stack */
  @media ${device.mobile} {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.75rem;
    padding: 1.15rem 1.15rem 1.25rem;
  }

  /* Desktop / tablet: fee box | job copy | pricing | saved box */
  @media ${device.minTablet} {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: clamp(0.85rem, 1.8vw, 1.5rem);
    padding: 1.1rem 1.75rem 1.15rem;
  }
`;

// ── Ledger Layout Components ────────────────────────────────────────────────

const LedgerJobInfo = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
  min-width: 0;

  @media ${device.minTablet} {
    flex: 1 1 9rem;
    max-width: 14rem;
  }

  @media ${device.mobile} {
    align-items: center;
    text-align: center;
  }
`;

const ledgerEndCapStyles = `
  text-align: center;
  padding: 1rem 1.35rem;
  border-radius: 10px;
  width: 8.75rem;
  min-height: 4.25rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 0 0 8.75rem;
`;

const LedgerEndCap = styled.div`
  ${ledgerEndCapStyles}
  background: ${(p) =>
    p.$variant === "saved"
      ? "linear-gradient(135deg, rgba(16, 185, 129, 0.16), rgba(16, 185, 129, 0.09))"
      : "linear-gradient(135deg, rgba(255, 87, 34, 0.16), rgba(255, 87, 34, 0.09))"};
  border: 1px solid
    ${(p) =>
      p.$variant === "saved"
        ? "rgba(16, 185, 129, 0.38)"
        : "rgba(255, 87, 34, 0.38)"};

  @media ${device.mobile} {
    ${({ $variant }) =>
      $variant === "search"
        ? `
      width: auto;
      max-width: none;
      min-height: 0;
      flex: 0 0 auto;
      padding: 0.18rem 0.55rem;
      border-radius: 6px;
      flex-direction: row;
      align-items: baseline;
      gap: 0.28rem;
      background: rgba(255, 87, 34, 0.08);
      border-color: rgba(255, 87, 34, 0.22);
    `
        : `
      width: auto;
      max-width: none;
      min-height: 0;
      flex: 0 0 auto;
      padding: 0.32rem 0.85rem;
    `}
  }
`;

const LedgerMetricAmount = styled.div`
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  font-size: 1.125rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$tone === "green" ? "#047857" : "#c2410c")};

  .symbol {
    font-size: 0.72em;
    font-weight: 800;
    line-height: 1;
  }

  .symbol.suffix {
    margin-left: 0.02em;
  }

  @media ${device.mobile} {
    font-size: 0.875rem;
  }
`;

const LedgerMetricLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-top: 0.3rem;
  color: ${(p) => (p.$tone === "green" ? "#065f46" : "#9a3412")};

  @media ${device.mobile} {
    margin-top: 0;
    font-size: 0.62rem;
    letter-spacing: 0.06em;
  }
`;

const JobTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${SLATE_800};
  margin: 0 0 0.35rem;
  line-height: 1.25;
  text-wrap: balance;
`;

const JobDesc = styled.p`
  font-size: 0.875rem;
  color: ${SLATE_600};
  margin: 0;
  font-weight: 500;
  line-height: 1.45;
  max-width: 100%;
  text-wrap: balance;

  @media ${device.mobile} {
    text-align: center;
  }
`;

const TierLabel = styled.span`
  display: block;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #b45309;
  margin-bottom: 0.35rem;
`;

const WorkTypesLine = styled.p`
  font-size: 0.78rem;
  color: ${SLATE_500};
  margin: 0.55rem 0 0;
  line-height: 1.45;
  font-weight: 500;
  max-width: 100%;

  @media ${device.mobile} {
    text-align: center;
  }
`;

const LedgerPricing = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem 1.1rem;
  width: 100%;

  @media ${device.minTablet} {
    flex: 1.65 1 20rem;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    column-gap: clamp(0.5rem, 1.5vw, 1rem);
    row-gap: 0;
    max-width: none;
  }

  @media ${device.mobile} {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    width: 100%;
    max-width: 18rem;
  }
`;

const PriceColumn = styled.div`
  text-align: center;
  flex: 1;
  min-width: 0;

  @media ${device.minTablet} {
    width: 100%;
    max-width: 11.25rem;
    justify-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  @media ${device.mobile} {
    width: 100%;
    max-width: 16rem;
    padding: 0.85rem 1rem;
    border-radius: 10px;
    box-sizing: border-box;
    background: ${(p) =>
      p.$variant === "retail"
        ? "rgba(239, 68, 68, 0.06)"
        : "rgba(16, 185, 129, 0.06)"};
    border: 1px solid
      ${(p) =>
        p.$variant === "retail"
          ? "rgba(239, 68, 68, 0.18)"
          : "rgba(16, 185, 129, 0.2)"};
  }
`;

const PriceLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  margin-bottom: 0.35rem;
  color: ${SLATE_700};
  border-left: 3px solid ${(p) => (p.$isRetail ? "#dc2626" : "#059669")};
  padding-left: 0.45rem;
  text-align: left;

  @media ${device.minTablet} {
    white-space: nowrap;
    /* Center under the amount so the vs divider reads between two prices, not two empty halves */
    text-align: center;
    border-left: none;
    border-bottom: 2px solid ${(p) => (p.$isRetail ? "#dc2626" : "#059669")};
    padding-left: 0;
    padding-bottom: 0.35rem;
    margin-bottom: 0.45rem;
    width: 100%;
    max-width: 11rem;
    box-sizing: border-box;
  }

  @media ${device.mobile} {
    text-align: center;
    border-left: none;
    border-bottom: 2px solid ${(p) => (p.$isRetail ? "#dc2626" : "#059669")};
    padding-left: 0;
    padding-bottom: 0.35rem;
    margin-bottom: 0.45rem;
  }
`;

const PriceAmount = styled.div`
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  font-size: 1.28rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$isExpensive ? "#b91c1c" : "#047857")};

  span.symbol {
    font-size: 0.72em;
    font-weight: 800;
    line-height: 1;
    color: inherit;
  }

  span.symbol.suffix {
    margin-left: 0.02em;
  }
`;

const VsIndicatorLedger = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${SLATE_500};
  flex: 0 0 auto;
  line-height: 1;
  padding: 0 0.35rem;
  text-align: center;

  @media ${device.minTablet} {
    grid-column: 2;
    justify-self: center;
    align-self: center;
  }

  @media ${device.mobile} {
    font-size: 0.8rem;
  }
`;

const SavingsFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const ExampleTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${SLATE_800};
  margin: 0 0 0.5rem;
`;

const ExampleDesc = styled.p`
  font-size: 1rem;
  color: ${SLATE_300};
  margin: 0;
  font-weight: 500;
`;

const BidsContainer = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1.2rem;
    align-items: center;
  }
`;

const BadBid = styled.div`
  flex: 1;
  text-align: center;
  padding: 1.2rem 1rem;
  background: rgba(239, 68, 68, 0.08);
  border: 2px solid rgba(239, 68, 68, 0.25);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 120px;
  box-sizing: border-box;
`;

const GoodBid = styled.div`
  flex: 1;
  text-align: center;
  padding: 1.2rem 1rem;
  background: rgba(16, 185, 129, 0.08);
  border: 2px solid rgba(16, 185, 129, 0.25);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 120px;
  box-sizing: border-box;
`;

const BidLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;

  /* Color based on parent context - red for expensive, green for reasonable */
  ${({ $isRetail }) =>
    $isRetail
      ? `color: #dc2626; background: rgba(220, 38, 38, 0.1); padding: 0.2rem 0.5rem; border-radius: 4px;`
      : `color: #059669; background: rgba(5, 150, 105, 0.1); padding: 0.2rem 0.5rem; border-radius: 4px;`}
`;

const BidAmount = styled.div`
  font-size: 1.8rem;
  font-weight: 900;
  margin-bottom: 0.3rem;
  color: ${(props) => (props.$isExpensive ? "#dc2626" : "#059669")};

  /* Style the ~ and + symbols more subtly */
  span.symbol {
    font-size: 1.4rem;
    font-weight: 600;
    color: ${SLATE_300};
    margin-right: 0.1rem;
  }
`;

const BidNote = styled.div`
  font-size: 0.8rem;
  color: ${SLATE_300};
  font-style: italic;
`;

const VsIndicator = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${SLATE_800};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  min-height: 80px;

  @media ${device.mobile} {
    transform: rotate(90deg);
    min-height: auto;
    padding: 0.25rem;
  }
`;

const SavingsAmount = styled.div`
  text-align: center;
  font-size: 1.2rem;
  font-weight: 800;
  color: #059669;
  background: linear-gradient(
    135deg,
    rgba(16, 185, 129, 0.15),
    rgba(16, 185, 129, 0.08)
  );
  padding: 1rem 1.2rem;
  border-radius: 12px;
  border: 2px solid rgba(16, 185, 129, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
  margin-top: 0.5rem;

  /* Emphasize as the "Bottom Line Result" */
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: -1px;
    left: 20%;
    right: 20%;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(16, 185, 129, 0.6),
      transparent
    );
  }
`;

const InfoIcon = styled.span`
  font-size: 1rem;
  cursor: help;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const SavingsCallout = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  max-width: 800px;

  @media ${device.mobile} {
    padding: 1.2rem;
    gap: 1rem;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const CalloutIcon = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: rgba(255, 255, 255, 0.95);

  svg {
    width: 26px;
    height: 26px;
  }

  @media ${device.mobile} {
    width: 40px;
    height: 40px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const CalloutText = styled.div`
  flex: 1;

  @media ${device.mobile} {
    width: 100%;
    align-self: stretch;
    text-align: center;
  }
`;

const CalloutTitle = styled.h4`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${WHITE};
  margin: 0 0 0.8rem;
  line-height: 1.3;
`;

const CalloutDesc = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.6;
  font-weight: 400;
`;

const SafetyDisclaimer = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  line-height: 1.4;
  max-width: 700px;
  font-weight: 400;
  font-style: italic;
  background: rgba(255, 255, 255, 0.03);
  padding: 0.8rem 1.2rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(3px);

  @media ${device.mobile} {
    font-size: 0.75rem;
    padding: 0.7rem 1rem;
  }
`;

const VendorSafeguard = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  line-height: 1.3;
  max-width: 600px;
  font-weight: 400;
  font-style: italic;
  background: rgba(255, 255, 255, 0.02);
  padding: 0.6rem 1rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(2px);

  @media ${device.mobile} {
    font-size: 0.7rem;
    padding: 0.5rem 0.8rem;
  }
`;

export default HomeMain;
